import { computed, ref } from "vue"
import { Identifiable } from "features/common/shared/polymorphic"

export type LoadMoreFunction = (
  offset: number,
  pageSize: number
) => Promise<Identifiable[]>

/**
 *  The trailing offset is on the last loaded item position. The leading offset is on the first not-loaded item position.
 *  Example (* = loaded) :
 *  0*
 *  1*
 *  2*
 *  3   <- leading offset
 *  4
 *  5*  <- trailing offset
 *  6*
 *  7*
 *
 *  To calculate the left items, we can subtract the leading offset from the trailing offset.
 *  Example => 5 - 3 = 2
 */

export const useOmissionLoaderInternal = (
  totalCount: number,
  pageSize: number,
  initialLeadingCount: number,
  initialTrailingCount: number,
  loadMore: LoadMoreFunction,
  emitLeading: (items: any[]) => void,
  emitTrailing: (items: any[]) => void
) => {
  const loadOnlyLeading = initialLeadingCount > 0 && initialTrailingCount === 0
  const leadingOffset = ref(0)
  const trailingOffset = ref(totalCount)
  const leadingLoading = ref(0)
  const trailingLoading = ref(0)

  const remainingCount = computed(() => {
    return trailingOffset.value - leadingOffset.value
  })

  const isLastPage = computed(() => {
    return remainingCount.value <= pageSize
  })

  const allLoaded = computed(() => {
    // Add items that are currently loading to the remaining count
    return remainingCount.value <= 0
  })

  const nextPageSize = computed(() => {
    return Math.min(pageSize, remainingCount.value, totalCount)
  })

  const loadLeading = async (pageSize: number = nextPageSize.value) => {
    leadingLoading.value += pageSize
    const offset = leadingOffset.value

    // Ensure that both markers never overlap
    leadingOffset.value = Math.min(
      leadingOffset.value + pageSize,
      trailingOffset.value
    )
    try {
      const newLeadingItems = await loadMore(offset, pageSize)
      emitLeading(newLeadingItems)
    } finally {
      leadingLoading.value -= pageSize
    }
  }

  const loadRemaining = async () => {
    return loadLeading(remainingCount.value)
  }

  const loadTrailing = async (_pageSize: number = nextPageSize.value) => {
    if (_pageSize === 0) return
    trailingLoading.value += _pageSize
    const offset = trailingOffset.value - _pageSize

    // Ensure that both markers never overlap
    trailingOffset.value = Math.max(
      trailingOffset.value - _pageSize,
      leadingOffset.value
    )
    try {
      const newTrailingItems = await loadMore(offset, _pageSize)
      emitTrailing(newTrailingItems)
    } finally {
      trailingLoading.value -= _pageSize
    }
  }

  if (totalCount > 0) {
    loadLeading(Math.min(initialLeadingCount, nextPageSize.value))
    if (!loadOnlyLeading && totalCount > initialLeadingCount) {
      loadTrailing(Math.min(initialTrailingCount, nextPageSize.value))
    }
  }

  return {
    isLastPage,
    loadLeading,
    loadTrailing,
    loadRemaining,
    allLoaded,
    leadingLoading,
    trailingLoading,
    remainingCount,
    loadOnlyLeading,
    nextPageSize,
  }
}
